import React, { useCallback, useMemo, useState } from "react";

import Decimal from "decimal.js";
import { orderBy } from "lodash";

import useGetPortfolioPlanClassificationById, {
  FinMasonError,
} from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanClassificationById";
import { AssetClassCategory } from "@fartherfinance/frontend/api/PerformanceGroups/requests/getHoldings";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import {
  PortfolioPlanId,
  SecurityId,
} from "@fartherfinance/frontend/api/Types";

import { getSecuritiesFromCategories } from "@src/multiCustodian/components/PortfolioPlanV2/ClassificationTable/classificationTableHelpers";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import BorderBox from "@src/sharedComponents/BorderBox/BorderBox";
import BorderBoxBody from "@src/sharedComponents/BorderBox/BorderBoxBody";
import BorderBoxHeader from "@src/sharedComponents/BorderBox/BorderBoxHeader";
import { FartherTableSortLabel } from "@src/sharedComponents/Table/Components";
import Text from "@src/sharedComponents/Typography/Text";
import displayTitleFromSecurity from "@src/utils/displayTitleFromSecurity";

import { SecurityRow } from "./Row/Row";

import styles from "./Table.module.css";

interface Props {
  portfolioPlanId: PortfolioPlanId;
}

type SecurityData = {
  securityId: SecurityId;
  weight: Decimal;
  description?: string | null;
  name?: string | null;
  type?: string;
  identifier?: string | null;
};

type SortingConfig = {
  field: "name" | "weight";
  direction: "asc" | "desc";
};

const PortfolioHoldingsConstructionTable: React.FC<Props> = ({
  portfolioPlanId,
}) => {
  const auth = useRequestAuth();
  const classification = useGetPortfolioPlanClassificationById(
    portfolioPlanId,
    auth
  );

  //** Recursively map all nested securities and label them by Category **//
  const flatClassificationSecurity = useMemo(() => {
    const data = classification.data;

    if (data === undefined) {
      return null;
    }

    const topLevelCategories = Object.keys(data);

    return topLevelCategories.flatMap((topCat) => {
      const topLevelCategory = data[topCat];
      return getSecuritiesFromCategories(topLevelCategory.categories, topCat);
    });
  }, [classification.data]);

  const securitiesId =
    flatClassificationSecurity?.map((security) => security.securityId) ?? null;
  const securitiesRespData = useSecuritiesBySecurityIds(securitiesId, auth);

  //** Merge portfolio data with security master information **//
  const bulkMerge = useMemo(() => {
    if (!securitiesRespData.data) {
      return [];
    }

    const newData: SecurityData[] = (flatClassificationSecurity ?? []).map(
      (security) => {
        const securityDescription = securitiesRespData.data.find(
          (sec) => sec.id === security.securityId
        );

        if (securityDescription === undefined) {
          return {
            securityId: security.securityId,
            weight: security.weight,
            type: security.topCategory,
            description: "N/A",
            name: "N/A",
            identifier: "N/A",
          };
        }

        return {
          securityId: security.securityId,
          weight: security.weight,
          description: securityDescription?.description,
          name: securityDescription?.name,
          type: security.topCategory,
          identifier: displayTitleFromSecurity(securityDescription),
        };
      }
    );

    return newData;
  }, [flatClassificationSecurity, securitiesRespData.data]);

  const _unknownAssetInPortfolio =
    classification.hasError && classification.error instanceof FinMasonError;

  const [sortConfig, setSortConfig] = useState<SortingConfig>({
    field: "weight",
    direction: "desc",
  });

  // We could have done this in one statement but it would've been hard to read
  const securitiesSortedByWeight = useMemo(() => {
    return orderBy(
      bulkMerge,
      [
        (sec) =>
          sec.name === "CASH" || sec.identifier === "US DOLLARS" ? -1 : 0, // send CASH to the end of the list, regardless of its weight
        (sec) => sec.weight.toNumber(),
        (sec) => sec.identifier,
      ],
      ["desc", sortConfig.direction, "asc"]
    );
  }, [bulkMerge, sortConfig.direction]);

  const securitiesSortedByName = useMemo(() => {
    return orderBy(
      bulkMerge,
      [
        (sec) =>
          sec.name === "CASH" || sec.identifier === "US DOLLARS" ? -1 : 0, // send CASH to the end of the list, regardless of its weight
        (sec) => sec.identifier,
      ],
      ["desc", sortConfig.direction]
    );
  }, [bulkMerge, sortConfig.direction]);

  const securitiesSortedList = useMemo(
    () =>
      sortConfig.field === "weight"
        ? securitiesSortedByWeight
        : securitiesSortedByName,
    [securitiesSortedByName, securitiesSortedByWeight, sortConfig.field]
  );

  const updateSorting = useCallback((fieldName: "name" | "weight") => {
    return () => {
      setSortConfig((prev) => {
        const isSameField = prev.field === fieldName;
        const newDirection =
          isSameField && prev.direction === "asc" ? "desc" : "asc";

        return {
          field: fieldName, // Update the sorting field
          direction: newDirection, // Toggle or reset the direction
        };
      });
    };
  }, []);

  return (
    <BorderBox style={{ minWidth: 400 }}>
      <BorderBoxHeader
        className={styles.borderBoxHeader}
        start={<Text variant="title2">{"Model Holdings"}</Text>}
      />
      <BorderBoxBody>
        <TableHeader sortConfig={sortConfig} onPress={updateSorting} />
        {securitiesRespData.isLoading || classification.isLoading ? (
          <SecurityRow isLoading hasError={false} data={undefined} />
        ) : securitiesRespData.hasError || classification.hasError ? (
          <SecurityRow
            hasError
            error={new Error("unknown")}
            isLoading={false}
            data={undefined}
          />
        ) : (
          securitiesSortedList.map((security) => (
            <SecurityRow
              key={security.securityId}
              data={{
                ticker: security.identifier || "N/A",
                description: security.description || "N/A",
                weight: security.weight.toNumber().toLocaleString("en-US", {
                  style: "percent",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
                type: AssetClassCategory.parse(security.type),
              }}
              isLoading={false}
              hasError={false}
            />
          ))
        )}
      </BorderBoxBody>
    </BorderBox>
  );
};

// this will take a carry function that will return the function that updates the state
const TableHeader = ({
  onPress,
  sortConfig,
}: {
  sortConfig: SortingConfig;
  onPress: (fieldName: "name" | "weight") => () => void;
}) => (
  <div className={styles.holdingsHeader}>
    <FartherTableSortLabel
      direction={sortConfig.direction}
      active={sortConfig.field === "name"}
      onClick={onPress("name")}
    >
      <Text variant={"heading1"} color={"text"} style={{ cursor: "pointer" }}>
        Name
      </Text>
    </FartherTableSortLabel>

    <FartherTableSortLabel
      direction={sortConfig.direction}
      active={sortConfig.field === "weight"}
      onClick={onPress("weight")}
    >
      <Text variant={"heading1"} color={"text"} style={{ cursor: "pointer" }}>
        Weight
      </Text>
    </FartherTableSortLabel>
  </div>
);

export default PortfolioHoldingsConstructionTable;
